import IconSelect from '@ben/orion/icon-select';
import { Input, InputVariant } from '@ben/orion/input';
import MantineRichTextEditor from '@ben/orion/mantine-rich-text-editor';
import PhoneInput from '@ben/orion/phone-input';
import type { WidgetProps } from '@rjsf/utils';
import { useCallback, useEffect } from 'react';
import { useTheme } from 'styled-components';
import SelectWidget from './select-widget';

// Map from RJSF type/format to input type attribute
// Defaults to `text` if no matches
const TYPE_MAP: Record<string, string> = {
  integer: 'number',
  number: 'number',
  email: 'email',
  password: 'password',
};

const InputWidget = (props: WidgetProps) => {
  const {
    id,
    schema,
    label,
    value,
    placeholder,
    disabled,
    onChange,
    onBlur,
    onFocus,
    rawErrors,
    hideError,
    readonly,
    uiSchema,
    registry,
  } = props;
  const formContext = registry?.formContext;
  const theme = useTheme();
  const typeKey = schema.format ?? (schema.type as string);
  const type = TYPE_MAP[typeKey] ?? 'text';
  const variant =
    schema.format === 'textarea' ? InputVariant.TextArea : InputVariant.Text;

  const onRichTextChange = useCallback(
    (text: string) => {
      onChange(text || undefined);
    },
    [onChange]
  );

  const onTextChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange(event.target?.value || undefined);
    },
    [onChange]
  );
  const onTextBlur = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      onBlur(id, event.target?.value);
    },
    [id, onBlur]
  );
  const onTextFocus = useCallback(
    (event: React.FocusEvent<HTMLInputElement>) => {
      onFocus(id, event.target?.value);
    },
    [id, onFocus]
  );

  useEffect(() => {
    // if a form input is dependent on another it needs clearing when it returns to readonly
    if (readonly && schema?.properties?.clearWhenReadOnly && value) {
      onChange(undefined);
    }
    // We specifically only want to listen to readonly changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readonly]);

  const hasError = rawErrors && rawErrors.length > 0 && !hideError;

  const errorMessage = rawErrors?.join(', ');

  if (schema.format === 'phone') {
    return (
      <PhoneInput
        id={id}
        name={label}
        value={value}
        onChange={onChange}
        hasError={hasError}
        errorMessage={errorMessage}
        placeholder={placeholder}
        width="100%"
        disabled={disabled || readonly}
      />
    );
  }

  if (schema.format === 'rich-text') {
    return (
      <MantineRichTextEditor
        label={label}
        value={value ?? ''}
        onChange={onRichTextChange}
        errorMessage={hasError ? errorMessage : undefined}
        onImageUpload={formContext?.onImageUpload}
        imagePasteEnabled={!!formContext?.onImageUpload}
        iframeEnabled={formContext?.iframeEnabled}
        disabled={disabled || readonly}
        headings={formContext?.richTextHeadings}
      />
    );
  }

  if (schema.format === 'icon') {
    return (
      <IconSelect
        id={id}
        value={value}
        onChange={onChange}
        label={label}
        placeholder={placeholder}
        errorMessage={errorMessage}
      />
    );
  }

  if (
    ['country', 'nationality', 'nationality-names'].includes(
      schema.format ?? ''
    )
  ) {
    return <SelectWidget {...props} />;
  }

  return (
    <Input
      id={id}
      type={type}
      variant={variant}
      name={label}
      value={value ?? ''}
      placeholder={placeholder}
      disabled={disabled || readonly}
      hasError={hasError}
      errorMessage={errorMessage}
      onChange={onTextChange}
      onBlur={onTextBlur}
      onFocus={onTextFocus}
      width="100%"
      height={
        // we never want the height to be less than our input.text.height value
        // but if maxLength is longer than twice the height then we want to increase the height proportionally
        schema.format === 'textarea' &&
        schema?.maxLength &&
        schema.maxLength > Number.parseInt(theme.input.text.height, 10) * 2
          ? `${(schema.maxLength ?? 0) * 0.5}px`
          : undefined
      }
      hintText={schema?.description}
      maxLength={schema?.maxLength}
      min={schema?.minimum}
      max={schema?.maximum}
      step={
        schema.type === 'number'
          ? (schema.multipleOf ?? 'any')
          : schema.multipleOf
      }
      hideMaxLength={uiSchema?.hideMaxLength}
      clearable={uiSchema?.clearable || formContext?.clearableFields}
    />
  );
};

export default InputWidget;
